'use client'

import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, useForm } from 'react-hook-form'
import { forwardRef, useEffect, useState, type ComponentProps } from 'react'
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Link,
  HStack,
  Checkbox,
  Text,
  FormErrorMessage,
  InputGroup,
  InputLeftAddon,
  Select,
  Image,
  Flex,
} from '@chakra-ui/react'
import { Button } from '../ui/button'
import { useRouter, useSearchParams } from 'next/navigation'
import NextLink from 'next/link'
import { useLoginMutation } from '@/src/mutations/auth/login-mutation'
import { useSignUpMutation } from '@/src/mutations/auth/signup-mutation'
import { toast } from 'sonner'
import { getDeviceType, isPWA, maxAgeDate, toTitleCase } from '@/src/lib/utils'
import GoogleLogin from '@/src/app/(lyk-appp)/auth/google-login'
import { useCountriesQuery } from '@/src/queries/auth/country-query'
import PasswordInput from './password-input'
import AppleLogin from '@/src/app/(lyk-appp)/auth/apple-login'
import { ENABLED_COUNTRIES } from '@/lib/enabled_countries'
import { GetTurnstileToken } from '@/src/lib/cloudflareTurnstile'
import { useLocationQuery } from '@/src/queries/guest/location-query'
import GoogleLoginRedirection from '@/src/app/(lyk-appp)/auth/google-login-redirection'
import { Icons } from '../icons'
import IosPwaLoginSignupModal from '../PWA/pwa-signup-login-ios-popup'
import { usePostHogEvents, EVENTS } from '@/lib/posthog-events'
interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  formType: 'login' | 'signup'
  isTv?: boolean
  onAuthSuccess?: () => void
}

export interface UserAuthFormData {
  email: string
  password: string
  fullName?: string
  confirmPassword?: string
  country?: string
  phone?: string
  gender?: 'male' | 'female' | 'non-binary' | 'other'
  dob?: string
}

const UserAuthForm = ({ formType, isTv }: UserAuthFormProps) => {
  const {
    handleSubmit,
    register,
    setError,
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm<UserAuthFormData>()

  const [countryCode, setCountryCode] = useState('91')
  const [isFetchingSignupToken, setIsFetchingSignupToken] = useState(false)
  const loginMutation = useLoginMutation()
  const signupMutation = useSignUpMutation()
  const { trackEvent } = usePostHogEvents()
  const countries = useCountriesQuery()
  const { data: locationData } = useLocationQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [socialType, setSocialType] = useState('')
  const closeModal = () => {
    setIsOpen(false)
  }
  const inputStyles = {
    size: {
      base: 'md',
    },
    rounded: 'md',
    border: '1px solid #E5E5E5',
    px: {
      base: '3',
      md: '4',
    },
    fontSize: {
      base: 'sm',
      md: 'md',
    },
  }

  const labelStyles = {
    fontSize: {
      base: 'sm',
      md: 'md',
    },
    mt: {
      base: '1',
      md: '1',
    },
    fontWeight: '500',
    marginBottom: '1px',
    color: '#525252',
  }

  const parseIsoDate = (value?: string) => {
    if (!value) return null
    const [year, month, day] = value.split('-').map(Number)
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null
    }
    return new Date(year, month - 1, day)
  }

  const toIsoDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const DatePickerInput = forwardRef<
    HTMLInputElement,
    ComponentProps<typeof Input>
  >((props, ref) => (
    <Input
      ref={ref}
      {...inputStyles}
      _placeholder={{ color: 'black', opacity: 1 }}
      {...props}
    />
  ))
  DatePickerInput.displayName = 'DatePickerInput'

  const router = useRouter()
  const searchParams = useSearchParams()
  const queryCode = searchParams.get('code')
  const handlePasswordFlow = () => {
    router.push('/auth/password')
  }

  const selectedCountry = watch('country')
  const countryCodeByCountry = countries.data?.find(
    (country) => country.id === selectedCountry
  )?.dialCode
  const dialCodeLength = countryCode.replace(/\D/g, '').length
  const phoneNumberLength = Math.min(10, 12 - dialCodeLength)
  const maxDateValue = parseIsoDate(maxAgeDate)

  useEffect(() => {
    if (selectedCountry) {
      setCountryCode(countryCodeByCountry || '')
    }
  }, [countryCodeByCountry, selectedCountry])

  useEffect(() => {
    if (getValues('phone')) {
      void trigger('phone')
    }
  }, [countryCode, getValues, trigger])

  const onSubmit = async (data: UserAuthFormData) => {
    if (formType === 'login') {
      if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(data.email) &&
        !/^[0-9]{10}$/.test(data.email)
      ) {
        setError('email', {
          type: 'manual',
          message: 'Invalid email address or phone number!',
        })
        return
      }

      const inputType = data.email.includes('@') ? 'email' : 'phone'
      loginMutation.mutate(
        {
          email: data.email,
          password: data.password,
          type: 'normal',
          inputType,
        },
        {
          onSuccess: (user) => {
            toast.success('Login successful!')
            trackEvent(EVENTS.USER_LOGGED_IN, {
              account_type: 'normal',
              provider: inputType,
              is_tv: !!isTv,
            })

            // if (user.verifiedUser == '0') {
            //   router.replace('/auth/verify')
            //   return
            // }
            // router.replace('/home')
          },
          onError: (error) => {
            toast.error(error.message)
            setError('root', {
              type: 'manual',
              message: error.message,
            })
          },
        }
      )
    } else {
      if (data.password.length > 16) {
        setError('password', {
          type: 'manual',
          message: "Password can't be longer than 16 characters.",
        })
        return
      }
      if (data.password !== data.confirmPassword) {
        setError('confirmPassword', {
          type: 'manual',
          message: 'Passwords do not match',
        })
        return
      }
      if (
        data.phone &&
        !new RegExp(`^[0-9]{${phoneNumberLength}}$`).test(data.phone)
      ) {
        setError('phone', {
          type: 'manual',
          message: `Phone number must be ${phoneNumberLength} digits`,
        })
        return
      }
      setIsFetchingSignupToken(true)
      const cfToken = await GetTurnstileToken(
        'cft-UserSignUp',
        'cft-container-user-auth-form'
      )
      // console.log('Signup Turnstile token:', cfToken)

      if (!cfToken) {
        setIsFetchingSignupToken(false)
        setError('root', {
          type: 'manual',
          message: 'Unable to generate Turnstile token.',
        })
        return
      }

      signupMutation.mutate(
        {
          email: data.email,
          password: data.password,
          name: data.fullName!,
          type: 'normal',
          countryCode: data.phone ? countryCode : countryCodeByCountry || '',
          countryId: data.country || '',
          phone: data.phone || '',
          gender: data.gender!,
          dob: data.dob,
          cfToken: cfToken || '',
        },
        {
          onSuccess: async () => {
            toast.success('Signup successful!')
            trackEvent(EVENTS.USER_SIGNED_UP, {
              account_type: 'normal',
              provider: 'email',
              is_tv: !!isTv,
            })
            // router.replace('/auth/verify')
          },
          onError: (error) => {
            toast.error(error.message)
            setError('root', {
              type: 'manual',
              message: error.message,
            })
          },
          onSettled: () => {
            setIsFetchingSignupToken(false)
          },
        }
      )
    }
  }
  useEffect(() => {
    if (locationData?.countryCode === 'IN') {
      setValue('country', '99')
    } else {
      setValue('country', '226')
    }
  }, [locationData])
  function openInBrowser(url: string) {
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
  return (
    <>
      <div id="cft-container-user-auth-form" />
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <VStack
          alignItems={{ base: 'flex-start', md: 'flex-start' }}
          className="w-full px-0"
        >
          {formType === 'signup' && (
            <FormControl isRequired isInvalid={!!errors.fullName}>
              <FormLabel htmlFor="fullName" {...labelStyles}>
                Full Name
              </FormLabel>
              <Input
                {...inputStyles}
                {...register('fullName', {
                  required: 'This field is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum length should be 3 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message:
                      'Your name can’t contain special characters or number. Please try again!',
                  },
                })}
              />
              <FormErrorMessage>
                <>{errors.fullName && errors.fullName.message}</>
              </FormErrorMessage>
            </FormControl>
          )}
          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel htmlFor="email" {...labelStyles}>
              {formType === 'signup'
                ? 'Email Address'
                : 'Email Address or Phone'}
            </FormLabel>
            <Input
              type={formType === 'signup' ? 'email' : 'text'}
              {...inputStyles}
              {...register('email', {
                required: 'This field is required',
                pattern:
                  formType === 'signup'
                    ? {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: 'Invalid email address',
                      }
                    : undefined,
              })}
            />
            <FormErrorMessage>
              <>{errors.email && errors.email.message}</>
            </FormErrorMessage>
          </FormControl>
          {formType == 'signup' && (
            <>
              <FormControl isRequired isInvalid={!!errors.country}>
                <FormLabel htmlFor="country" {...labelStyles}>
                  Country
                </FormLabel>
                <Select
                  {...register('country', {
                    required: 'This field is required',
                  })}
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  size={{
                    base: 'md',
                  }}
                  placeholder="Select Country"
                  border="1px solid #E5E5E5"
                  value={getValues('country')}
                >
                  {countries.data
                    ?.filter((country) =>
                      ENABLED_COUNTRIES.includes(country.name.toLowerCase())
                    )
                    .map((country) => (
                      <option
                        value={country.id}
                        key={`${country.id}-${country.iso}-${country.name}`}
                      >
                        {toTitleCase(country.name)}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage>
                  <>{errors.country && errors.country.message}</>
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel htmlFor="phone" {...labelStyles}>
                  Phone Number
                </FormLabel>
                <InputGroup>
                  <InputLeftAddon
                    border={'1px solid #E5E5E5'}
                    minW={'60px'}
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    px={0}
                  >
                    <Select
                      fontSize={{
                        base: 'sm',
                        md: 'md',
                      }}
                      id="countryCode"
                      name="countryCode"
                      size={{
                        base: 'md',
                      }}
                      border={'none'}
                      _focusVisible={{
                        border: 'none',
                        boxShadow: 'none',
                      }}
                      isDisabled
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value)
                      }}
                    >
                      {countries.data
                        ?.filter((country) =>
                          ENABLED_COUNTRIES.includes(country.name.toLowerCase())
                        )
                        .map((country) => (
                          <option
                            value={country.dialCode}
                            key={`${country.id}-${country.iso}-${country.dialCode}`}
                          >
                            +{country.dialCode}
                          </option>
                        ))}
                    </Select>
                  </InputLeftAddon>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    maxLength={phoneNumberLength}
                    {...inputStyles}
                    {...register('phone', {
                      minLength: {
                        value: phoneNumberLength,
                        message: `Phone number must be ${phoneNumberLength} digits`,
                      },
                      maxLength: {
                        value: phoneNumberLength,
                        message: `Phone number must be ${phoneNumberLength} digits`,
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Only numbers are allowed',
                      },
                    })}
                  />
                </InputGroup>
                <FormErrorMessage>
                  <>{errors.phone && errors.phone.message}</>
                </FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.gender}>
                <FormLabel htmlFor="gender" {...labelStyles}>
                  Gender
                </FormLabel>
                <Select
                  {...register('gender', {
                    required: 'This field is required',
                  })}
                  placeholder="Select Gender"
                  border="1px solid #E5E5E5"
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  size={{
                    base: 'md',
                  }}
                >
                  <option value="Male" key="Male">
                    Male
                  </option>
                  <option value="Female" key="Female">
                    Female
                  </option>
                  <option value="Non Binary" key="Non Binary">
                    Non Binary
                  </option>
                </Select>
                <FormErrorMessage>
                  <>{errors.gender && errors.gender.message}</>
                </FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.dob}>
                <FormLabel {...labelStyles}>Date of Birth</FormLabel>
                <Controller
                  control={control}
                  name="dob"
                  rules={{
                    required: 'This field is required',
                    validate: (value) => {
                      if (value) {
                        const dob = parseIsoDate(value)
                        if (!dob) {
                          return 'Please select a valid date'
                        }
                        const diff = new Date().getTime() - dob.getTime()
                        const age = Math.floor(
                          diff / (1000 * 60 * 60 * 24 * 365)
                        )
                        if (age < 13) {
                          return 'You must be at least 13 years old!'
                        }
                      }
                      return true
                    },
                  }}
                  render={({ field }) => (
                    <DatePicker
                      selected={parseIsoDate(field.value)}
                      onChange={(date: any) =>
                        field.onChange(
                          date instanceof Date ? toIsoDate(date) : ''
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      dateFormat="MM-dd-yyyy"
                      maxDate={maxDateValue || undefined}
                      placeholderText="mm-dd-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      customInput={
                        <DatePickerInput id="dob" isInvalid={!!errors.dob} />
                      }
                    />
                  )}
                />
                <FormErrorMessage>
                  <>{errors.dob && errors.dob.message}</>
                </FormErrorMessage>
              </FormControl>

              <Flex
                mt={2}
                gap={2}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Image boxSize={5} src="/account/info.svg" alt="Info" />
                <Text fontSize={12} fontWeight={400} opacity={0.7}>
                  LYKSTAGE will like to use this information to personalise your
                  experience. Your information is never shared with any other
                  user on LYKSTAGE. To check our Privacy Policy,&nbsp;
                  <Link
                    color="lyk-black"
                    fontSize={12}
                    fontWeight={600}
                    href="/privacypolicy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    click here.
                  </Link>
                </Text>
              </Flex>
            </>
          )}

          <FormControl isRequired isInvalid={!!errors.password}>
            <FormLabel htmlFor="password" {...labelStyles}>
              Password
            </FormLabel>
            <PasswordInput
              inputStyles={inputStyles}
              register={register}
              regName="password"
              formType={formType}
            />
            <FormErrorMessage>
              <>{errors.password && errors.password.message}</>
            </FormErrorMessage>
          </FormControl>
          {formType === 'signup' && (
            <FormControl isRequired isInvalid={!!errors.confirmPassword}>
              <FormLabel htmlFor="confirmPassword" {...labelStyles}>
                Confirm Password
              </FormLabel>
              <PasswordInput
                inputStyles={inputStyles}
                register={register}
                regName="confirmPassword"
                formType={formType}
              />
              <FormErrorMessage>
                <>{errors.confirmPassword && errors.confirmPassword.message}</>
              </FormErrorMessage>
            </FormControl>
          )}
          {formType === 'login' && (
            <Box mt={[1]}>
              <Checkbox
                name="1"
                colorScheme="green"
                size={{ base: 'sm', md: 'md' }}
              >
                Remember Password
              </Checkbox>
            </Box>
          )}
          {formType === 'login' && (
            <Box mt={[2]} mb={[2]}>
              <Link
                onClick={handlePasswordFlow}
                size="xs"
                fontSize={{
                  base: 'sm',
                  md: 'md',
                }}
                textDecoration={'underline'}
              >
                Forgot password?
              </Link>
            </Box>
          )}
          {errors.root && (
            <Text
              color="red"
              fontSize={{
                base: 'sm',
                md: 'md',
              }}
            >
              {errors.root.message}
            </Text>
          )}
          <Button
            type="submit"
            className="font-regular mt-3 w-full rounded-lg tracking-wide text-white"
            isLoading={
              loginMutation.isPending ||
              signupMutation.isPending ||
              isFetchingSignupToken
            }
          >
            {formType === 'signup' ? 'Proceed' : 'Login'}
          </Button>
        </VStack>
      </form>
      {isPWA() && getDeviceType() === 'iPhone' ? (
        <HStack
          mt={[4]}
          display={'flex'}
          justifyContent={{ base: 'center', md: 'left' }}
          alignItems={'center'}
          flexDir={'row'}
          className="w-full"
          flexWrap={'wrap'}
        >
          <Button
            className="font-base flex-1 border border-gray bg-white text-black hover:bg-lightergray "
            onClick={(e) => {
              e.preventDefault()
              setSocialType('Google')
              setIsOpen(true)
            }}
          >
            <Icons.google className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>
          <Button
            className="font-base flex-1 border border-gray bg-white text-black hover:bg-lightergray"
            onClick={(e) => {
              e.preventDefault()
              setSocialType('Apple')
              setIsOpen(true)
            }}
          >
            <Icons.Apple className="mr-2 h-5 w-5" />
            Continue with Apple
          </Button>
        </HStack>
      ) : (
        <HStack
          mt={[4]}
          display={'flex'}
          justifyContent={{ base: 'center', md: 'left' }}
          alignItems={'center'}
          flexDir={'row'}
          className="w-full"
          flexWrap={'wrap'}
        >
          {isPWA() ? (
            <GoogleLoginRedirection setError={setError} />
          ) : (
            <GoogleLogin setError={setError} />
          )}
          <AppleLogin setError={setError} />
        </HStack>
      )}
      {formType === 'login' ? (
        <Text
          mt={[4]}
          fontSize={{
            base: 'md',
            md: 'md',
          }}
          fontWeight={'regular'}
        >
          Don&apos;t have an account?
          <NextLink
            href={
              isTv
                ? `/auth/tvsignup?user=normal${queryCode ? `&code=${queryCode}` : ''}`
                : `/auth/signup?user=normal`
            }
            className="pl-1 font-medium text-green underline"
          >
            Sign up
          </NextLink>
        </Text>
      ) : (
        <Text
          mt={[4]}
          fontSize={{
            base: 'md',
            md: 'md',
          }}
          fontWeight={'regular'}
        >
          Already have an account?{' '}
          <NextLink
            href={
              isTv
                ? `/auth/tvlogin?user=normal${queryCode ? `&code=${queryCode}` : ''}`
                : `/auth/login?user=normal`
            }
            className="pl-1 font-medium text-green underline"
          >
            Login
          </NextLink>
        </Text>
      )}
      <IosPwaLoginSignupModal
        isOpen={isOpen}
        onClose={closeModal}
        formType={formType}
        type={socialType}
      />
    </>
  )
}

export default UserAuthForm
